import { openDB, type DBSchema } from 'idb';
import type { QueueItem, Reading } from './types';

interface PvDb extends DBSchema {
  readings: { key: string; value: Reading; indexes: { byYear: number } };
  queue: { key: string; value: QueueItem; indexes: { byCapturedAt: string } };
  geo: { key: string; value: GeoEntry };
}

/** Koordinate einer Adresse (lat/lon fehlen, wenn Azure Maps nichts gefunden hat). */
export interface GeoEntry { key: string; lat?: number; lon?: number; at: number }

const dbPromise = openDB<PvDb>('pv-zaehlerablesung', 2, {
  upgrade(db, oldVersion) {
    if (oldVersion < 1) {
      const readings = db.createObjectStore('readings', { keyPath: 'id' });
      readings.createIndex('byYear', 'Importjahr');
      const queue = db.createObjectStore('queue', { keyPath: 'localId' });
      queue.createIndex('byCapturedAt', 'capturedAt');
    }
    if (oldVersion < 2) db.createObjectStore('geo', { keyPath: 'key' });
  }
});

export async function cacheReadings(items: Reading[]): Promise<void> {
  const db = await dbPromise;
  const tx = db.transaction('readings', 'readwrite');
  await tx.store.clear();
  await Promise.all(items.map(item => tx.store.put(item)));
  await tx.done;
}

export async function getCachedReadings(year: number): Promise<Reading[]> {
  return (await dbPromise).getAllFromIndex('readings', 'byYear', year);
}

export async function enqueue(item: QueueItem): Promise<void> { await (await dbPromise).put('queue', item); }
export async function getQueue(): Promise<QueueItem[]> { return (await dbPromise).getAll('queue'); }
export async function deleteQueueItem(id: string): Promise<void> { await (await dbPromise).delete('queue', id); }
export async function updateQueueItem(item: QueueItem): Promise<void> { await (await dbPromise).put('queue', item); }
export async function queueCount(): Promise<number> { return (await dbPromise).count('queue'); }

export async function getGeoEntries(keys: string[]): Promise<GeoEntry[]> {
  const db = await dbPromise;
  const found = await Promise.all(keys.map(key => db.get('geo', key)));
  return found.filter((entry): entry is GeoEntry => entry !== undefined);
}

export async function putGeoEntries(entries: GeoEntry[]): Promise<void> {
  const db = await dbPromise;
  const tx = db.transaction('geo', 'readwrite');
  await Promise.all(entries.map(entry => tx.store.put(entry)));
  await tx.done;
}
